/**
 * Quiz Engine - Reusable quiz component with immediate feedback
 *
 * Features:
 * - Random question sampling from pool
 * - Immediate feedback on each answer
 * - Progress tracking
 * - Pass/fail threshold checking
 */

import { useState, useCallback, useMemo } from 'react';
import { Check, X, AlertCircle, Trophy, RotateCcw } from 'lucide-react';
import type { QuizQuestion, QuizAttempt, QuizType } from '../../types/onboarding';
import { QUIZ_CONFIG } from '../../lib/onboarding/constants';
import { sampleQuestions, calculateScore } from '../../lib/onboarding/quizData';
import { cn } from '../../lib/utils';

interface QuizEngineProps {
  quizType: QuizType;
  questionPool: QuizQuestion[];
  onComplete: (result: QuizAttempt) => void;
  previousAttempts?: number;
  previousScore?: number | null;
}

type QuizState = 'intro' | 'question' | 'feedback' | 'result';

export default function QuizEngine({
  quizType,
  questionPool,
  onComplete,
  previousAttempts = 0,
  previousScore = null,
}: QuizEngineProps) {
  const config = QUIZ_CONFIG[quizType];

  // Sample questions for this attempt
  const [questions] = useState(() =>
    sampleQuestions(questionPool, config.questionsPerAttempt)
  );

  const [quizState, setQuizState] = useState<QuizState>('intro');
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [answers, setAnswers] = useState<Record<string, number>>({});
  const [showExplanation, setShowExplanation] = useState(false);

  const currentQuestion = questions[currentQuestionIndex];
  const passThreshold = config.passThreshold * 100;

  // Calculate final result
  const result = useMemo(() => {
    if (Object.keys(answers).length !== questions.length) return null;
    return calculateScore(questions, answers);
  }, [questions, answers]);

  // Handle answer selection
  const handleSelectAnswer = useCallback((index: number) => {
    if (selectedAnswer !== null) return; // Already answered
    setSelectedAnswer(index);
    setShowExplanation(true);
  }, [selectedAnswer]);

  // Handle next question
  const handleNext = useCallback(() => {
    if (selectedAnswer === null) return;

    // Save answer
    setAnswers((prev) => ({
      ...prev,
      [currentQuestion.id]: selectedAnswer,
    }));

    // Move to next question or results
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex((prev) => prev + 1);
      setSelectedAnswer(null);
      setShowExplanation(false);
    } else {
      setQuizState('result');
    }
  }, [selectedAnswer, currentQuestion, currentQuestionIndex, questions.length]);

  // Handle quiz completion
  const handleComplete = useCallback(() => {
    if (!result) return;

    const attempt: QuizAttempt = {
      quizType,
      score: result.correct,
      totalQuestions: result.total,
      percentage: result.percentage,
      passed: result.percentage >= passThreshold,
      attemptedAt: new Date().toISOString(),
      questionIds: questions.map((q) => q.id),
    };

    onComplete(attempt);
  }, [result, quizType, passThreshold, questions, onComplete]);

  // Handle retry
  const handleRetry = useCallback(() => {
    // Reset quiz with new questions
    window.location.reload(); // Simple approach - could be improved
  }, []);

  // Render intro screen
  if (quizState === 'intro') {
    return (
      <QuizIntro
        quizType={quizType}
        questionCount={config.questionsPerAttempt}
        passThreshold={passThreshold}
        previousAttempts={previousAttempts}
        previousScore={previousScore}
        onStart={() => setQuizState('question')}
      />
    );
  }

  // Render result screen
  if (quizState === 'result' && result) {
    return (
      <QuizResult
        result={result}
        passThreshold={passThreshold}
        quizType={quizType}
        onContinue={handleComplete}
        onRetry={handleRetry}
      />
    );
  }

  // Render question
  return (
    <div className="space-y-6">
      {/* Progress Bar */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2 text-sm">
          <span style={{ color: 'var(--color-text-muted)' }}>
            Question {currentQuestionIndex + 1} of {questions.length}
          </span>
          <span style={{ color: 'var(--color-accent)' }}>
            {Math.round(((currentQuestionIndex + 1) / questions.length) * 100)}%
          </span>
        </div>
        <div
          className="h-2 rounded-full overflow-hidden"
          style={{ backgroundColor: 'var(--color-bg-tertiary)' }}
        >
          <div
            className="h-full rounded-full transition-all duration-300"
            style={{
              backgroundColor: 'var(--color-accent)',
              width: `${((currentQuestionIndex + 1) / questions.length) * 100}%`,
            }}
          />
        </div>
      </div>

      {/* Question */}
      <div
        className="p-6 rounded-xl"
        style={{
          backgroundColor: 'var(--color-bg-secondary)',
          border: '1px solid var(--color-border)',
        }}
      >
        <h3
          className="text-lg font-semibold mb-6"
          style={{ color: 'var(--color-text-primary)' }}
        >
          {currentQuestion.question}
        </h3>

        {/* Options */}
        <div className="space-y-3">
          {currentQuestion.options.map((option, index) => {
            const isSelected = selectedAnswer === index;
            const isCorrect = index === currentQuestion.correctIndex;
            const showCorrectness = showExplanation;

            return (
              <button
                key={index}
                onClick={() => handleSelectAnswer(index)}
                disabled={showExplanation}
                className={cn(
                  'w-full flex items-center gap-4 p-4 rounded-xl text-left transition-all',
                  'disabled:cursor-default'
                )}
                style={{
                  backgroundColor: showCorrectness
                    ? isCorrect
                      ? 'rgba(34, 197, 94, 0.15)'
                      : isSelected
                      ? 'rgba(239, 68, 68, 0.15)'
                      : 'var(--color-bg-tertiary)'
                    : isSelected
                    ? 'var(--color-accent-muted)'
                    : 'var(--color-bg-tertiary)',
                  border: `2px solid ${
                    showCorrectness
                      ? isCorrect
                        ? 'var(--color-success)'
                        : isSelected
                        ? 'var(--color-error)'
                        : 'transparent'
                      : isSelected
                      ? 'var(--color-accent)'
                      : 'transparent'
                  }`,
                }}
              >
                {/* Option Letter */}
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{
                    backgroundColor: showCorrectness
                      ? isCorrect
                        ? 'var(--color-success)'
                        : isSelected
                        ? 'var(--color-error)'
                        : 'var(--color-bg-secondary)'
                      : isSelected
                      ? 'var(--color-accent)'
                      : 'var(--color-bg-secondary)',
                    color: showCorrectness || isSelected
                      ? 'white'
                      : 'var(--color-text-secondary)',
                  }}
                >
                  {showCorrectness ? (
                    isCorrect ? (
                      <Check className="w-4 h-4" />
                    ) : isSelected ? (
                      <X className="w-4 h-4" />
                    ) : (
                      String.fromCharCode(65 + index)
                    )
                  ) : (
                    String.fromCharCode(65 + index)
                  )}
                </div>

                {/* Option Text */}
                <span
                  className="flex-1"
                  style={{
                    color: showCorrectness
                      ? isCorrect
                        ? 'var(--color-success)'
                        : isSelected
                        ? 'var(--color-error)'
                        : 'var(--color-text-secondary)'
                      : 'var(--color-text-primary)',
                  }}
                >
                  {option}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Explanation */}
      {showExplanation && (
        <div
          className={cn(
            'p-4 rounded-xl animate-in fade-in slide-in-from-bottom-2 duration-300'
          )}
          style={{
            backgroundColor:
              selectedAnswer === currentQuestion.correctIndex
                ? 'rgba(34, 197, 94, 0.1)'
                : 'rgba(239, 68, 68, 0.1)',
            border: `1px solid ${
              selectedAnswer === currentQuestion.correctIndex
                ? 'var(--color-success)'
                : 'var(--color-error)'
            }`,
          }}
        >
          <div className="flex items-start gap-3">
            {selectedAnswer === currentQuestion.correctIndex ? (
              <Check
                className="w-5 h-5 flex-shrink-0 mt-0.5"
                style={{ color: 'var(--color-success)' }}
              />
            ) : (
              <AlertCircle
                className="w-5 h-5 flex-shrink-0 mt-0.5"
                style={{ color: 'var(--color-error)' }}
              />
            )}
            <div>
              <p
                className="font-medium mb-1"
                style={{
                  color:
                    selectedAnswer === currentQuestion.correctIndex
                      ? 'var(--color-success)'
                      : 'var(--color-error)',
                }}
              >
                {selectedAnswer === currentQuestion.correctIndex
                  ? 'Correct!'
                  : 'Incorrect'}
              </p>
              <p
                className="text-sm"
                style={{ color: 'var(--color-text-secondary)' }}
              >
                {currentQuestion.explanation}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Next Button */}
      {showExplanation && (
        <button
          onClick={handleNext}
          className="w-full py-3 rounded-xl font-semibold transition-all hover:scale-[1.01]"
          style={{
            backgroundColor: 'var(--color-accent)',
            color: 'var(--color-bg)',
          }}
        >
          {currentQuestionIndex < questions.length - 1
            ? 'Next Question'
            : 'See Results'}
        </button>
      )}
    </div>
  );
}

// ============================================================================
// Quiz Intro Component
// ============================================================================

interface QuizIntroProps {
  quizType: QuizType;
  questionCount: number;
  passThreshold: number;
  previousAttempts: number;
  previousScore: number | null;
  onStart: () => void;
}

function QuizIntro({
  quizType,
  questionCount,
  passThreshold,
  previousAttempts,
  previousScore,
  onStart,
}: QuizIntroProps) {
  const title = quizType === 'security' ? 'Security Quiz' : 'System Knowledge Quiz';

  return (
    <div
      className="p-8 rounded-xl text-center"
      style={{
        backgroundColor: 'var(--color-bg-secondary)',
        border: '1px solid var(--color-border)',
      }}
    >
      <div
        className="w-16 h-16 mx-auto mb-6 rounded-full flex items-center justify-center"
        style={{ backgroundColor: 'var(--color-accent-muted)' }}
      >
        <AlertCircle className="w-8 h-8" style={{ color: 'var(--color-accent)' }} />
      </div>

      <h3
        className="text-xl font-bold mb-2"
        style={{ color: 'var(--color-text-primary)' }}
      >
        {title}
      </h3>

      <p
        className="text-sm mb-6 max-w-md mx-auto"
        style={{ color: 'var(--color-text-secondary)' }}
      >
        Test your understanding with {questionCount} questions randomly selected from
        our question pool. You need {passThreshold}% to pass.
      </p>

      {/* Stats */}
      <div
        className="inline-flex items-center gap-4 mb-6 p-4 rounded-lg"
        style={{ backgroundColor: 'var(--color-bg-tertiary)' }}
      >
        <div className="text-center">
          <p
            className="text-2xl font-bold"
            style={{ color: 'var(--color-text-primary)' }}
          >
            {questionCount}
          </p>
          <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
            Questions
          </p>
        </div>
        <div
          className="w-px h-10"
          style={{ backgroundColor: 'var(--color-border)' }}
        />
        <div className="text-center">
          <p
            className="text-2xl font-bold"
            style={{ color: 'var(--color-accent)' }}
          >
            {passThreshold}%
          </p>
          <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
            To Pass
          </p>
        </div>
        {previousAttempts > 0 && (
          <>
            <div
              className="w-px h-10"
              style={{ backgroundColor: 'var(--color-border)' }}
            />
            <div className="text-center">
              <p
                className="text-2xl font-bold"
                style={{ color: 'var(--color-text-secondary)' }}
              >
                {previousAttempts}
              </p>
              <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                Attempts
              </p>
            </div>
          </>
        )}
      </div>

      {/* Previous Score */}
      {previousScore !== null && (
        <p
          className="text-sm mb-4"
          style={{
            color:
              previousScore >= passThreshold
                ? 'var(--color-success)'
                : 'var(--color-warning)',
          }}
        >
          Your previous score: {previousScore}%
        </p>
      )}

      <button
        onClick={onStart}
        className="px-8 py-3 rounded-xl font-semibold transition-all hover:scale-105"
        style={{
          backgroundColor: 'var(--color-accent)',
          color: 'var(--color-bg)',
        }}
      >
        Start Quiz
      </button>

      <p
        className="mt-4 text-xs"
        style={{ color: 'var(--color-text-muted)' }}
      >
        Questions are randomly selected, so each attempt will be different
      </p>
    </div>
  );
}

// ============================================================================
// Quiz Result Component
// ============================================================================

interface QuizResultProps {
  result: { correct: number; total: number; percentage: number };
  passThreshold: number;
  quizType: QuizType;
  onContinue: () => void;
  onRetry: () => void;
}

function QuizResult({
  result,
  passThreshold,
  quizType,
  onContinue,
  onRetry,
}: QuizResultProps) {
  const passed = result.percentage >= passThreshold;

  return (
    <div
      className="p-8 rounded-xl text-center"
      style={{
        backgroundColor: passed
          ? 'rgba(34, 197, 94, 0.1)'
          : 'rgba(239, 68, 68, 0.1)',
        border: `1px solid ${passed ? 'var(--color-success)' : 'var(--color-error)'}`,
      }}
    >
      <div
        className="w-20 h-20 mx-auto mb-6 rounded-full flex items-center justify-center"
        style={{
          backgroundColor: passed ? 'var(--color-success)' : 'var(--color-error)',
        }}
      >
        {passed ? (
          <Trophy className="w-10 h-10" style={{ color: 'white' }} />
        ) : (
          <X className="w-10 h-10" style={{ color: 'white' }} />
        )}
      </div>

      <h3
        className="text-2xl font-bold mb-2"
        style={{ color: passed ? 'var(--color-success)' : 'var(--color-error)' }}
      >
        {passed ? 'Quiz Passed!' : 'Not Quite There'}
      </h3>

      <p
        className="text-4xl font-bold mb-2"
        style={{ color: 'var(--color-text-primary)' }}
      >
        {result.percentage}%
      </p>

      <p
        className="text-sm mb-6"
        style={{ color: 'var(--color-text-secondary)' }}
      >
        You got {result.correct} out of {result.total} questions correct
        {!passed && `. You need ${passThreshold}% to pass.`}
      </p>

      <div className="flex gap-3 justify-center">
        {passed ? (
          <button
            onClick={onContinue}
            className="px-8 py-3 rounded-xl font-semibold transition-all hover:scale-105"
            style={{
              backgroundColor: 'var(--color-success)',
              color: 'white',
            }}
          >
            Continue
          </button>
        ) : (
          <>
            <button
              onClick={onRetry}
              className="flex items-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all hover:scale-105"
              style={{
                backgroundColor: 'var(--color-bg-tertiary)',
                color: 'var(--color-text-secondary)',
              }}
            >
              <RotateCcw className="w-4 h-4" />
              Try Again
            </button>
          </>
        )}
      </div>

      {!passed && (
        <p
          className="mt-4 text-sm"
          style={{ color: 'var(--color-text-muted)' }}
        >
          Review the training materials and try again. New questions will be
          selected.
        </p>
      )}
    </div>
  );
}
